import classNames from 'classnames';
import React, {useState} from 'react';

import {IconButton, Text} from '@albert-io/atomic';
import Assignment from '../Assignments/Assignment';
import {canReadTeacherFeedback} from '../Assignments/teacherFeedback';
import useMarkEvaluationSeen from './hooks/useMarkEvaluationSeen';
import useReviewGradeBanner from './hooks/useReviewGradeBanner';

type Props = PropsWithClassNameOptional & {
  /** Scopes the banner to the classroom the assignment list below it is showing. */
  classroomId?: string;
};

export default function ReviewGradeBanner({className, classroomId}: Props) {
  const [isDismissed, setIsDismissed] = useState(false);
  const {assignments, isEnabled, isLoading, refetch} = useReviewGradeBanner(classroomId);
  const markEvaluationSeen = useMarkEvaluationSeen();

  if (isDismissed || isLoading || !isEnabled || assignments.length === 0) {
    return null;
  }

  function handleDismiss() {
    setIsDismissed(true);
    // Best-effort: a failed mutation just means the assignment reappears next time the banner
    // loads, which is an acceptable degraded state rather than something to retry here.
    Promise.allSettled(assignments.map((assignment) => markEvaluationSeen(assignment.id))).then(
      () => refetch().catch(() => {})
    );
  }

  return (
    <div className={classNames(className, 'gap-x1 background-bold px-x2 py-x1-half flex flex-col')}>
      <div className='gap-x1 flex items-center'>
        <Text className='flex-1'>Review Grade</Text>
        <IconButton icon='xmark' label='Dismiss review grade' onClick={handleDismiss} size='s' />
      </div>
      <div className='gap-x2 rounded-x2 border-primary background-primary px-x2 py-x1-half flex flex-col border'>
        {assignments.map((assignment) => (
          <Assignment
            assignment={assignment}
            hasTeacherFeedback={canReadTeacherFeedback(assignment)}
            key={assignment.assignmentId}
            // Reading one is what marks it read; without this the X is the only way to clear the
            // banner, so a student who opens a single assignment still has to wipe the rest.
            onClick={() => markEvaluationSeen(assignment.id)}
          />
        ))}
      </div>
    </div>
  );
}
